import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Autocomplete, Paper, TextField } from '@mui/material';
import { debounce } from 'lodash';
import { CLIENT_BASIC_SEARCH_URL, clientSearchKeys, toSearchBody } from 'utils/clientSearch';
import { inputsStyle } from './styles';
import { InputMinLoader } from './inputMiniLoader';
import { TextRow } from '../table';
import { useNavigate, useLocation } from 'react-router-dom';
import { ErrMessage } from '../messages';
import { ErrorText, Images, useGlobalStyles, User } from 'utils';
import { SelectEnums } from './constants';
import { MinLoader } from '../loader';

export const SelectAutocompletePagination = ({
    title,
    defaultValue,
    name,
    handleSelect,
    style,
    disabled,
    error,
    placeholder,
    renderValue,
    customClass,
    uiType,
    outLabel,
    outStyle,
    startIcon,
    type,
    defaultStatuses,
    noError,
    typeError,
    currentSelected,
    autoSelect,
    sendParams,
    defaultList,
    filterOptions = (options) => options,
}) => {
    const classes = inputsStyle();
    const globalStyles = useGlobalStyles();
    const [value, setValue] = useState(null);
    const [options, setOptions] = useState([]);
    const [page, setPage] = useState(1);
    const [open, setOpen] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = 50;
    const navigate = useNavigate();
    const location = useLocation();
    const info = location?.state;
    const refreshClicked = useRef(false);
    const current = options?.find((i) => i.id === defaultValue);
    const defaultSelected = current ? current : currentSelected;
    const { userInfo } = User();

    useEffect(() => {
        if (autoSelect && !value && options?.length) {
            const currentStaff = options?.find((i) => i.id === userInfo?.id);
            handleChange(null, currentStaff || null);
        }
    }, [options]);

    const fetchOptions = useCallback(
        debounce(async (page, searchTerm, statuses, info, sendParams) => {
            try {
                setLoading(true);
                const apiUrl =
                    type === 'staff'
                        ? '/staff/basic/info'
                        : type === 'client'
                          ? CLIENT_BASIC_SEARCH_URL
                          : type === 'fullStaffList'
                            ? '/staff'
                            : '';

                const params = {
                    skip: (page - 1) * ITEMS_PER_PAGE,
                    limit: ITEMS_PER_PAGE,
                    statuses: statuses,
                    searchValue: searchTerm || undefined,
                    searchKey: searchTerm ? (type === 'staff' ? 'NAME' : clientSearchKeys.ANY) : undefined,
                    sortType: type === 'staff' ? 'fullName' : 'code',
                    orderType: 'ASC',
                    ...(sendParams || {}),
                };

                if (!info || !info?.showActive || info?.showActive === false) {
                    params.statuses = defaultStatuses;
                } else if (info?.showActive === true) {
                    params.statuses = [];
                }

                // Client search text is PHI — it goes in a POST body, never the URL.
                const response =
                    type === 'client'
                        ? await axios.post(CLIENT_BASIC_SEARCH_URL, toSearchBody(params), { auth: true })
                        : await axios.get(apiUrl, {
                              params,
                              auth: true,
                          });

                const data = response.data || [];
                setOptions((prevOptions) => (page === 1 ? [...data] : [...prevOptions, ...data]));
                setHasMore(data.length >= ITEMS_PER_PAGE);
            } catch (error) {
                if (error?.status === 403 && defaultList?.length) {
                    setOptions(defaultList);
                }
            } finally {
                setLoading(false);
            }
        }, 300),
        [type]
    );

    useEffect(() => {
        fetchOptions(page, searchTerm, defaultStatuses, info, sendParams);
    }, [page, searchTerm, sendParams, fetchOptions]);

    useEffect(() => {
        if (defaultValue && defaultSelected) {
            setValue(defaultSelected);
        } else if (!defaultValue) {
            setValue(null);
        }
    }, [defaultValue, defaultSelected]);

    useEffect(() => {
        if (info?.showActive === undefined || info?.showActive === false) {
            fetchOptions(page, searchTerm, defaultStatuses, info, sendParams);
        } else if (info?.showActive === true) {
            fetchOptions(page, searchTerm, [], info, sendParams);
        }
    }, [info?.showActive, sendParams]);

    const handleScroll = (event) => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
            if (hasMore && !loading) {
                setLoading(true);
                setPage((prevPage) => prevPage + 1);
            }
        }
    };

    const handleChange = (event, newValue) => {
        setValue(newValue);
        handleSelect({
            target: {
                value: newValue?.id || '',
                fullName: newValue?.fullName,
                full: newValue,
                name,
            },
        });
    };

    const handleInputChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
        setOptions([]);
        setHasMore(true);
    };

    return (
        <div className={style || ''}>
            {outLabel && (
                <p id='labelTitle' className={classes.labelTitle} style={{ ...outStyle }}>
                    {outLabel}
                </p>
            )}
            <Autocomplete
                style={{ width: '100%' }}
                className={
                    uiType === 'tableFilter'
                        ? classes.noChipLoads
                        : uiType
                          ? 'autocomplete-main-wrapper'
                          : disabled
                            ? classes.inputTextFieldDisabled
                            : customClass
                              ? customClass
                              : error === name
                                ? globalStyles.inputTextFieldError
                                : defaultValue
                                  ? globalStyles.inputTextFieldBlue
                                  : classes.inputTextField
                }
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                    setOpen(false);
                    setSearchTerm('');
                    if (!options?.length) {
                        fetchOptions(1, '', defaultStatuses, info, sendParams);
                    }
                }}
                onChange={(event, newValue) => {
                    handleChange(event, newValue);
                    setOpen(false);
                    event?.target?.blur();
                    document?.activeElement?.blur();
                }}
                loading={loading}
                value={value}
                options={filterOptions ? filterOptions(options) : options}
                getOptionLabel={(option) => renderValue(option) || ''}
                isOptionEqualToValue={(option, value) => {
                    const optionId = option?.id || option?._id;
                    const valueId = value?.id || value?._id;
                    return optionId === valueId;
                }}
                filterOptions={(x) => x}
                ListboxProps={{ onScroll: handleScroll, style: { paddingBottom: '42px' } }}
                disabled={disabled}
                PaperComponent={({ children, ...paperProps }) => (
                    <Paper {...paperProps}>
                        {children}
                        <div onMouseDown={(e) => e.preventDefault()}>
                            <div className="refresh-btn-wrapper">
                                <button
                                    onClick={() => fetchOptions(page, searchTerm, defaultStatuses, info, sendParams)}
                                    className="refresh-btn"
                                >
                                    <div className="refresh-btn-image-box">
                                        {loading ? (
                                            <MinLoader height={16} width={17} color={'#0C66E4'} margin={'0'} position={'relative'} />
                                        ) : (
                                            <img src={Images.refreshSelect} alt="refresh" />
                                        )}
                                    </div>
                                    <p>Refresh</p>
                                </button>
                            </div>
                        </div>
                    </Paper>
                )}
                renderOption={(props, option) => {
                    const { key, ...restProps } = props;
                    return (
                        <li key={key} {...restProps}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                {option.img && <img src={option.img} alt="icon" />}
                                <TextRow name={renderValue(option) || ' '} textWidth={7} />
                            </div>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        label={title}
                        variant="outlined"
                        onChange={handleInputChange}
                        slotProps={{
                            input:
                                loading || startIcon
                                    ? {
                                          ...params.InputProps,
                                          endAdornment: loading && <InputMinLoader />,
                                          startAdornment: startIcon && <img src={startIcon} alt="icon" />,
                                      }
                                    : { ...params.InputProps },
                        }}
                    />
                )}
            />
            {!noError && (
                <ErrMessage text={error === name ? (typeError ? typeError : ErrorText.field) : ''} style={{ position: 'relative' }} />
            )}
        </div>
    );
};
